import { useState } from "react";
import { Button, Input } from "../ui";
import { formatSar } from "../../utils/billingMoney";
import { createServiceOrderAPI } from "../../utils/api/billing";
import { fillTemplate } from "./clientBillingText";

// genericServiceOrderModal: fields adapt to billingMode (qty input vs
// branch-checkbox grid vs month-count), server computes the real price.
const ServiceOrderModal = ({ service, branches, lang, text, onCancel, onDone }) => {
  const token = localStorage.getItem("auth-token");
  const isRtl = lang !== "en";
  const [quantity, setQuantity] = useState(1);
  const [branchIds, setBranchIds] = useState([]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggleBranch = (branchId) => {
    setBranchIds((current) =>
      current.includes(branchId)
        ? current.filter((id) => id !== branchId)
        : [...current, branchId],
    );
  };

  const effectiveQuantity =
    service.billingMode === "per_branch" ? branchIds.length : Number(quantity) || 0;
  const estimatedMinor = effectiveQuantity * (service.unitPriceMinor || 0);
  const canSubmit =
    service.billingMode === "per_branch" ? branchIds.length > 0 : effectiveQuantity > 0;

  const submit = async () => {
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await createServiceOrderAPI(token, {
        serviceCode: service.code,
        quantity: service.billingMode === "per_branch" ? undefined : effectiveQuantity,
        branchIds: service.billingMode === "per_branch" ? branchIds : undefined,
        note: note || undefined,
      });
      await onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const quantityLabel =
    service.billingMode === "monthly"
      ? text.monthsLabel
      : service.code === "AI_IMAGES"
        ? text.photosLabel
        : text.quantityLabel;

  return (
    <div className="billing-modal-backdrop" dir={isRtl ? "rtl" : "ltr"}>
      <div className="billing-modal">
        <h3>{fillTemplate(text.serviceOrderModalTitle, { service: lang === "en" ? service.nameEn : service.nameAr })}</h3>
        {error && <div className="billing-error">{error}</div>}

        {service.billingMode === "per_branch" ? (
          <>
            <strong>{text.selectBranches}</strong>
            <div className="cb-branch-picker">
              {(branches || []).map((branch) => {
                const id = String(branch._id);
                return (
                  <label key={id}>
                    <input
                      type="checkbox"
                      checked={branchIds.includes(id)}
                      onChange={() => toggleBranch(id)}
                    />
                    {branch.name}
                  </label>
                );
              })}
            </div>
          </>
        ) : service.billingMode === "fixed" ? null : (
          <Input
            type="number"
            min={1}
            max={service.maxItems || undefined}
            label={quantityLabel}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        )}

        <Input
          textarea
          label={text.noteLabel}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />

        <div className="billing-preview-box">
          <div>
            <span>{text.subtotal}</span>
            <strong>{formatSar(estimatedMinor, lang)}</strong>
          </div>
        </div>

        <div className="billing-modal-actions">
          <Button
            appearance="filled"
            loading={submitting}
            disabled={submitting || !canSubmit}
            onClick={submit}
          >
            {submitting ? text.submitting : text.confirmOrder}
          </Button>
          <Button appearance="ghost" disabled={submitting} onClick={onCancel}>
            {text.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceOrderModal;
